import type { Block, BlockType } from '@emailforge/schemas';
import { useState } from 'react';

/**
 * In-editor preview of a single block. This is a lightweight DOM preview —
 * NOT the production render. Production render happens on the server via
 * @emailforge/email-renderer and is shown in the Preview pane / cross-client.
 *
 * The optional `editor` bundle is passed by the canvas to enable interaction
 * inside `columns` blocks (drop zones, draggable children, hover toolbars).
 * When omitted (e.g. inside a read-only cross-client preview), columns
 * render as static.
 */
export type ColumnEditorBindings = {
  selectedId: string | null;
  onSelectChild: (id: string | null) => void;
  onDropNewIntoColumn: (params: {
    columnsBlockId: string;
    columnIndex: number;
    atIndex: number;
    type: BlockType;
  }) => void;
  onMoveColumnChild: (params: {
    columnsBlockId: string;
    blockId: string;
    toColumnIndex: number;
    toIndex: number;
  }) => void;
  onRemoveColumnChild: (columnsBlockId: string, blockId: string) => void;
};

const containerStyle = (b: Block): React.CSSProperties => {
  const p = 'props' in b ? (b.props as Record<string, unknown>) : {};
  return {
    paddingTop: (p.paddingTop as number | undefined) ?? 0,
    paddingBottom: (p.paddingBottom as number | undefined) ?? 0,
    paddingLeft: (p.paddingX as number | undefined) ?? 0,
    paddingRight: (p.paddingX as number | undefined) ?? 0,
    backgroundColor: (p.backgroundColor as string | undefined) ?? undefined,
  };
};

export function BlockPreview({
  block,
  editor,
}: {
  block: Block;
  editor?: ColumnEditorBindings;
}) {
  switch (block.type) {
    case 'hero':
      return (
        <div style={{ ...containerStyle(block), textAlign: block.props.alignment ?? 'center' }}>
          <div
            style={{
              fontFamily: 'Fraunces, Georgia, serif',
              fontSize: 30,
              fontWeight: 500,
              letterSpacing: '-0.015em',
              lineHeight: 1.1,
              color: '#2a251f',
              margin: '0 0 10px',
            }}
          >
            {block.props.title}
          </div>
          {block.props.subtitle && (
            <div style={{ fontSize: 14, color: '#5a4a38', lineHeight: 1.5, margin: 0 }}>
              {block.props.subtitle}
            </div>
          )}
          {block.props.imageUrl && (
            <img
              src={block.props.imageUrl}
              alt=""
              style={{ width: '100%', maxWidth: 560, height: 'auto', marginTop: 16 }}
            />
          )}
        </div>
      );
    case 'heading': {
      const sizes = { h1: 28, h2: 22, h3: 18 } as const;
      const Tag = (block.props.level ?? 'h2') as keyof JSX.IntrinsicElements;
      return (
        <div style={containerStyle(block)}>
          <Tag
            style={{
              fontFamily: 'Fraunces, Georgia, serif',
              fontSize: sizes[block.props.level ?? 'h2'],
              fontWeight: 500,
              letterSpacing: '-0.01em',
              textAlign: block.props.alignment ?? 'left',
              color: '#2a251f',
              margin: 0,
              lineHeight: 1.15,
            }}
          >
            {block.props.text}
          </Tag>
        </div>
      );
    }
    case 'text': {
      const textSizes = { xs: 12, sm: 13, md: 14.5, lg: 16 } as const;
      return (
        <div style={containerStyle(block)}>
          <p
            style={{
              fontSize: textSizes[block.props.size ?? 'md'],
              color: '#3a2f24',
              textAlign: block.props.alignment ?? 'left',
              margin: 0,
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
            }}
          >
            {block.props.text}
          </p>
        </div>
      );
    }
    case 'button': {
      const variants: Record<string, React.CSSProperties> = {
        primary: { background: '#c96442', color: 'white', border: '1px solid #a34d2d' },
        secondary: { background: '#2a251f', color: 'white', border: '1px solid #2a251f' },
        ghost: { background: 'transparent', color: '#2a251f', border: '1px solid #d9d0c0' },
      };
      const v = variants[block.props.variant ?? 'primary'];
      return (
        <div style={{ ...containerStyle(block), textAlign: block.props.alignment ?? 'center' }}>
          <a
            style={{
              ...v,
              padding: '10px 20px',
              borderRadius: 5,
              fontFamily: 'Geist, sans-serif',
              fontSize: 13.5,
              fontWeight: 500,
              textDecoration: 'none',
              display: block.props.fullWidth ? 'block' : 'inline-block',
              width: block.props.fullWidth ? '100%' : undefined,
              textAlign: 'center',
              boxSizing: 'border-box',
            }}
          >
            {block.props.label}
          </a>
        </div>
      );
    }
    case 'image':
      return (
        <div style={{ ...containerStyle(block), textAlign: block.props.alignment ?? 'center' }}>
          <img
            src={block.props.src}
            alt={block.props.alt ?? ''}
            style={{ width: '100%', maxWidth: block.props.width ?? 560, height: 'auto', borderRadius: 4 }}
          />
        </div>
      );
    case 'divider':
      return (
        <div style={containerStyle(block)}>
          <hr
            style={{
              border: 'none',
              borderTop: `${block.props.thickness ?? 1}px solid ${block.props.color ?? '#ece6d8'}`,
              margin: 0,
            }}
          />
        </div>
      );
    case 'spacer':
      return (
        <div
          style={{
            height: block.props.height,
            background:
              'repeating-linear-gradient(135deg, transparent 0 8px, oklch(0.95 0.005 85 / 0.5) 8px 10px)',
          }}
        />
      );
    case 'product':
      return (
        <div style={{ ...containerStyle(block), display: 'flex', gap: 14 }}>
          <img
            src={block.props.imageUrl}
            alt={block.props.name}
            style={{ width: 80, height: 80, borderRadius: 6, objectFit: 'cover' }}
          />
          <div>
            <div style={{ fontSize: 14, fontWeight: 550, color: '#2a251f', marginBottom: 2 }}>{block.props.name}</div>
            {block.props.description && (
              <div style={{ fontSize: 12, color: '#5a4a38', marginBottom: 4 }}>{block.props.description}</div>
            )}
            <div className="mono" style={{ fontSize: 12, color: '#2a251f', marginBottom: 4 }}>
              {block.props.price}
            </div>
            <a href={block.props.href} style={{ color: '#c96442', fontSize: 12, textDecoration: 'none' }}>
              View product →
            </a>
          </div>
        </div>
      );
    case 'footer':
      return (
        <div style={{ ...containerStyle(block), borderTop: '1px solid #ece6d8', textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: '#8b7a66', marginBottom: 6 }}>
            {block.props.companyName}
            {block.props.address && ` · ${block.props.address}`}
          </div>
          <div style={{ fontSize: 10.5, color: '#8b7a66' }}>
            <a href={block.props.unsubscribeUrl} style={{ color: '#8b7a66' }}>
              Unsubscribe
            </a>
          </div>
        </div>
      );
    case 'social':
      return (
        <div style={{ ...containerStyle(block), textAlign: block.props.alignment ?? 'center' }}>
          {block.props.icons.map((i) => (
            <a
              key={i.url}
              href={i.url}
              style={{
                display: 'inline-block',
                margin: '0 6px',
                textDecoration: 'none',
                color: '#5a4a38',
                fontSize: 12,
              }}
            >
              {i.network}
            </a>
          ))}
        </div>
      );
    case 'columns':
      return (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns:
              block.props.ratio === '1:1' ? '1fr 1fr' : block.props.ratio === '1:2' ? '1fr 2fr' : '2fr 1fr',
            gap: block.props.gap,
            padding: '12px 0',
          }}
        >
          {(block.children ?? []).map((col, i) => (
            <ColumnSlot
              key={i}
              columnsBlockId={block.id}
              columnIndex={i}
              children={col ?? []}
              editor={editor}
            />
          ))}
        </div>
      );
  }
}

/**
 * Renders a single column with its children. When `editor` is provided, also
 * renders drop zones between/around children so the user can:
 *   - drag a NEW block from the library into the column (`application/x-block-type`)
 *   - drag an EXISTING column child to a new slot, even across sibling columns
 *     (`application/x-column-child` — payload is the blockId)
 */
function ColumnSlot({
  columnsBlockId,
  columnIndex,
  children,
  editor,
}: {
  columnsBlockId: string;
  columnIndex: number;
  children: Block[];
  editor?: ColumnEditorBindings;
}) {
  if (!editor) {
    return (
      <div>
        {children.map((c) => (
          <BlockPreview key={c.id} block={c} />
        ))}
        {children.length === 0 && (
          <div
            className="mf-placeholder"
            style={{ height: 56, borderRadius: 'var(--r-2)', fontSize: 10 }}
          >
            EMPTY COLUMN
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 56,
        // dashed outline only when empty — once it has content, the children
        // own the visual weight
        ...(children.length === 0
          ? {
              border: '1.5px dashed var(--line)',
              borderRadius: 'var(--r-2)',
              padding: 8,
            }
          : null),
      }}
    >
      {children.length === 0 && (
        <ColumnDropZone columnsBlockId={columnsBlockId} columnIndex={columnIndex} atIndex={0} editor={editor} emptyHint />
      )}
      {children.map((c, idx) => (
        <div key={c.id}>
          <ColumnDropZone columnsBlockId={columnsBlockId} columnIndex={columnIndex} atIndex={idx} editor={editor} />
          <DraggableColumnChild
            block={c}
            columnsBlockId={columnsBlockId}
            editor={editor}
          />
        </div>
      ))}
      {children.length > 0 && (
        <ColumnDropZone
          columnsBlockId={columnsBlockId}
          columnIndex={columnIndex}
          atIndex={children.length}
          editor={editor}
        />
      )}
    </div>
  );
}

function DraggableColumnChild({
  block,
  columnsBlockId,
  editor,
}: {
  block: Block;
  columnsBlockId: string;
  editor: ColumnEditorBindings;
}) {
  const isSelected = editor.selectedId === block.id;
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('application/x-column-child', block.id);
        e.dataTransfer.effectAllowed = 'move';
        e.stopPropagation();
      }}
      onClick={(e) => {
        e.stopPropagation();
        editor.onSelectChild(block.id);
      }}
      style={{
        position: 'relative',
        outline: isSelected ? '2px solid var(--flame)' : '1px dashed transparent',
        outlineOffset: 2,
        borderRadius: 'var(--r-2)',
        cursor: 'grab',
      }}
    >
      <BlockPreview block={block} editor={editor} />
      {isSelected && (
        <button
          aria-label="Remove from column"
          onClick={(e) => {
            e.stopPropagation();
            editor.onRemoveColumnChild(columnsBlockId, block.id);
          }}
          style={{
            position: 'absolute',
            top: -8,
            right: -8,
            width: 18,
            height: 18,
            borderRadius: 9,
            border: 'none',
            background: 'var(--flame)',
            color: 'white',
            fontSize: 10,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}

function ColumnDropZone({
  columnsBlockId,
  columnIndex,
  atIndex,
  editor,
  emptyHint,
}: {
  columnsBlockId: string;
  columnIndex: number;
  atIndex: number;
  editor: ColumnEditorBindings;
  emptyHint?: boolean;
}) {
  const [active, setActive] = useState(false);
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = e.dataTransfer.types.includes('application/x-column-child')
          ? 'move'
          : 'copy';
      }}
      onDragEnter={() => setActive(true)}
      onDragLeave={() => setActive(false)}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setActive(false);
        const childId = e.dataTransfer.getData('application/x-column-child');
        if (childId) {
          editor.onMoveColumnChild({
            columnsBlockId,
            blockId: childId,
            toColumnIndex: columnIndex,
            toIndex: atIndex,
          });
          return;
        }
        const newType = e.dataTransfer.getData('application/x-block-type') as BlockType;
        if (newType) {
          editor.onDropNewIntoColumn({ columnsBlockId, columnIndex, atIndex, type: newType });
        }
      }}
      style={{
        height: active ? 22 : emptyHint ? 28 : 6,
        background: active ? 'var(--flame-soft)' : 'transparent',
        borderRadius: 4,
        margin: active ? '4px 0' : 0,
        transition: 'height 120ms ease',
        display: emptyHint ? 'flex' : 'block',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--ink-3)',
        fontSize: 10,
      }}
    >
      {emptyHint && !active && (
        <span className="mono" style={{ letterSpacing: 0.06, textTransform: 'uppercase' }}>
          DROP A BLOCK HERE
        </span>
      )}
    </div>
  );
}
